import { useEffect, useState, useCallback, useMemo } from 'react'
import toastService from '@/services/toast/service'
import { Gift, Info, Loader2, Plus, ScanEye } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AgencyXrayPanelSkeleton } from '@/components/skeletons'
import { RelativeTime } from '@/components/RelativeTime'
import { useIsViewer, VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer'
import {
  xrayAdminService,
  type AdminAgencyXraySummary,
} from '@/services/xrayAdmin'
import { formatDollarsFromCents, formatMoney, parseDollarsToCents, xrayProductLabel } from '@/lib/xray-pricing'

interface Props {
  agencyPublicId: string
}

export default function AgencyXrayPanel({ agencyPublicId }: Props) {
  const [summary, setSummary] = useState<AdminAgencyXraySummary | null>(null)
  const [loading, setLoading] = useState(true)
  const isViewer = useIsViewer()
  const [grantAmount, setGrantAmount] = useState('')
  const [grantReason, setGrantReason] = useState('')
  const [granting, setGranting] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const data = await xrayAdminService.getAgencySummary(agencyPublicId)
      setSummary(data)
    } catch (e: any) {
      toastService.error(e?.response?.data?.message || "Couldn't load X-Ray summary")
    } finally {
      setLoading(false)
    }
  }, [agencyPublicId])

  useEffect(() => { void reload() }, [reload])

  const submitGrant = async () => {
    const amountCents = parseDollarsToCents(grantAmount)
    if (amountCents == null || amountCents <= 0) {
      toastService.error('Enter an amount greater than zero')
      return
    }
    setGranting(true)
    try {
      const res = await xrayAdminService.grantFunds(agencyPublicId, amountCents, grantReason.trim() || undefined)
      toastService.success(
        `Added ${formatDollarsFromCents(res.addedCents)}, balance now ${formatDollarsFromCents(res.totalCents)}`,
      )
      setGrantAmount('')
      setGrantReason('')
      await reload()
    } catch (e: any) {
      toastService.error(e?.response?.data?.message || "Couldn't add funds")
    } finally {
      setGranting(false)
    }
  }

  // Wallet figures come straight from the API's getWallet-backed payload — the
  // same authoritative source as the customer's own billing card, so the two
  // surfaces can't drift. The bar reads as "spent this period out of what was
  // available" (available = balance + spend), so it never shrinks as money is
  // spent. The breakdown splits the spend by product.
  const walletBar = useMemo(() => {
    const wallet = summary?.wallet
    if (!summary?.hasSubscription || !wallet) return null
    const spent = wallet.spentThisPeriod.totalCents
    const total = spent + wallet.totalCents
    const usedPercent = total > 0 ? (Math.min(spent, total) / total) * 100 : 0
    const breakdown = Object.entries(wallet.spentThisPeriod.byProduct)
      .map(([key, s]) => ({ label: xrayProductLabel(key), value: s.cents }))
      .filter((b) => b.value > 0)
    return { spent, total, usedPercent, unusedPercent: total > 0 ? 100 - usedPercent : 0, breakdown }
  }, [summary])

  if (loading) return <AgencyXrayPanelSkeleton />

  if (!summary?.hasSubscription) {
    return (
      <Card className="py-0 gap-0">
        <CardContent className="py-12 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
            <ScanEye className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">No X-Ray subscription</h3>
          <p className="text-sm text-muted-foreground">
            This agency hasn't subscribed to X-Ray yet. Once they pick a plan, you'll be able to add funds to their wallet here.
          </p>
        </CardContent>
      </Card>
    )
  }

  const grantCents = parseDollarsToCents(grantAmount)
  const grantInvalid = grantCents == null || grantCents <= 0
  const xrayPlan = summary.xrayPlan!
  const wallet = summary.wallet!
  const autoRecharge = wallet.autoRecharge

  const priceLabel = xrayPlan.displayPrice == null ? null : `$${xrayPlan.displayPrice}/mo`

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Top row: X-Ray plan & wallet (mirrors the dashboard billing card) + Add funds */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* X-Ray — replicates the dashboard's billing ProductRow */}
        <Card className="py-0">
          <CardContent className="p-5 flex flex-col h-full">
            {/* Header: icon + name + plan badge + description, price on the right */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3.5 min-w-0">
                <div className="p-2.5 rounded-xl bg-purple-500/10 shrink-0 mt-0.5">
                  <ScanEye className="h-4 w-4 text-purple-500" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-sm">X-Ray</h4>
                    <Badge variant="outline" className="text-[10px] font-medium">
                      {xrayPlan.displayName}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Lead lookup with identity, financial, and professional data
                  </p>
                </div>
              </div>
              {priceLabel && (
                <span className="text-sm font-semibold tabular-nums shrink-0">{priceLabel}</span>
              )}
            </div>

            {/* Meta items */}
            <div className="mt-5 space-y-1.5 text-xs">
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-muted-foreground shrink-0">Wallet balance</span>
                <span className="font-medium text-right tabular-nums">
                  {formatDollarsFromCents(wallet.balanceCents)}
                </span>
              </div>
              {/* The allowance is units, not money, and is shown separately so
                  nobody reads the two as one pot. */}
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-muted-foreground shrink-0">Free Enriches</span>
                <span className="font-medium text-right tabular-nums">
                  {wallet.includedEnrich.remaining.toLocaleString()}
                  <span className="text-muted-foreground font-normal">
                    {' '}of {wallet.includedEnrich.perPeriod.toLocaleString()} left
                  </span>
                </span>
              </div>
              {wallet.periodEndUtc && (
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-muted-foreground shrink-0">
                    {wallet.cancelAtPeriodEnd ? 'Access until' : 'Next charge'}
                  </span>
                  <span className="font-medium text-right">
                    <RelativeTime date={wallet.periodEndUtc} />
                  </span>
                </div>
              )}
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-muted-foreground shrink-0">Auto-recharge</span>
                <span className="text-right">
                  {autoRecharge.enabled ? (
                    <>
                      <span className="font-medium">{formatMoney(autoRecharge.amountCents)}</span>
                      <span className="text-muted-foreground"> when below </span>
                      <span className="font-medium">{formatMoney(autoRecharge.thresholdCents)}</span>
                      <span className="text-muted-foreground"> · max </span>
                      <span className="font-medium">{formatMoney(autoRecharge.monthlyCapCents)}/month</span>
                      {autoRecharge.capReached && (
                        <span className="text-muted-foreground"> · cap reached</span>
                      )}
                    </>
                  ) : (
                    <span className="font-medium">Off</span>
                  )}
                </span>
              </div>
            </div>

            {/* Wallet bar */}
            <div className="mt-5">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-muted-foreground">Spent this period</span>
                <span className="flex items-center gap-1 font-medium tabular-nums">
                  {formatDollarsFromCents(walletBar!.spent)}
                  <span className="text-muted-foreground font-normal">
                    {' '}/ {formatDollarsFromCents(walletBar!.total)}
                  </span>
                  {walletBar!.breakdown.length > 0 && (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground/60 hover:text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <div className="flex flex-col gap-1 min-w-[180px] text-left">
                            {walletBar!.breakdown.map((b, i) => (
                              <div key={i} className="flex items-center justify-between gap-4">
                                <span className="text-background/70">{b.label}</span>
                                <span className="font-medium tabular-nums">{formatDollarsFromCents(b.value)}</span>
                              </div>
                            ))}
                            <div className="flex items-center justify-between gap-4 border-t border-background/20 pt-1 mt-0.5">
                              <span>Available this period</span>
                              <span className="font-semibold tabular-nums">{formatDollarsFromCents(walletBar!.total)}</span>
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </span>
              </div>
              <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-muted">
                <div
                  className="bg-purple-500 transition-all"
                  style={{ width: `${Math.min(walletBar!.usedPercent, 100)}%` }}
                />
                <div
                  className="bg-purple-500 opacity-25 transition-all"
                  style={{ width: `${walletBar!.unusedPercent}%` }}
                />
              </div>
              <div className="flex items-center gap-3 mt-1.5">
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <span className="inline-block w-2 h-2 rounded-full bg-purple-500 opacity-25" />
                  Balance {formatDollarsFromCents(wallet.totalCents)}
                </div>
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <span className="inline-block w-2 h-2 rounded-full bg-purple-500" />
                  Spent
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add funds */}
        <Card className="py-0">
          <CardContent className="p-4 flex flex-col h-full">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg shrink-0 bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400">
                <Gift className="h-5 w-5" />
              </div>
              <h3 className="text-base font-semibold">Add funds</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4 flex-1">
              Free funds at no charge. They never expire and are spent after this period's plan
              deposit. The customer sees them as "Funds added by the ROASForm team".
            </p>
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Amount</Label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    $
                  </span>
                  <Input
                    inputMode="decimal"
                    disabled={granting}
                    value={grantAmount}
                    onChange={(e) =>
                      setGrantAmount(e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))
                    }
                    className="pl-6 tabular-nums"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Reason (optional)</Label>
                <Input
                  type="text"
                  maxLength={500}
                  disabled={granting}
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  placeholder="e.g. goodwill for an outage"
                />
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="w-full block">
                    <Button className="w-full" disabled={granting || grantInvalid || isViewer} onClick={submitGrant}>
                      {granting ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="mr-1 h-4 w-4" />
                      )}
                      {granting ? 'Adding…' : 'Add funds'}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
              </Tooltip>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
